import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import block from 'bem-cn-lite';
import {useSearchParams} from 'react-router-dom';
import {Loader, Select, Text, TextInput} from '@gravity-ui/uikit';
import {AppContext} from '@app/context/context';
import useClientOptions from './hooks/useClientOptions';
import {useClientHistory} from './hooks/useClientHistory';

import './ClientSelect.scss';

const b = block('client-select-main-header');

export const ClientSelect = () => {
    const {state, emit} = useContext(AppContext);
    const userLogin = state.user?.login;
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedData, setSelectedData] = useState<{client: string; name: string} | null>(null);

    const [searchParams] = useSearchParams();
    const didInitFromUrl = useRef(false);

    const urlClient = decodeURIComponent(searchParams.get('client') || '');
    const urlName = decodeURIComponent(searchParams.get('name') || '');

    const [history, addToHistory] = useClientHistory();
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        // чтобы не перезатирать ручной выбор и не зациклиться
        if (didInitFromUrl.current) return;
        if (!urlClient || !urlName) return;

        // если вдруг уже выбран какой-то клиент в этой вкладке — не трогаем
        if (state.selectedClient?.isSelected) {
            didInitFromUrl.current = true;
            return;
        }

        const value = `${urlClient}::${urlName}`;

        setSelectedValues([value]);
        setSelectedData({client: urlClient, name: urlName});

        addToHistory({client: urlClient, name: urlName});

        emit('selectClient', {
            selectedClient: {client: urlClient, name: urlName, isSelected: true}
        });
        emit('clientSelectionError', {isError: false});

        didInitFromUrl.current = true;
    }, [urlClient, urlName, state.selectedClient?.isSelected, addToHistory]);

    const {
        items,
        isLoading,
        isDataLoaded,
        hasMore,
        loadInitialData,
        loadMoreData,
        handleSearchChange,
        searchText
    } = useClientOptions(userLogin);

    const filteredHistory = useMemo(() => {
        if (!searchText.trim()) {
            return history;
        }

        const lower = searchText.trim().toLowerCase();
        return history.filter(
            (h) => h.client.toLowerCase().includes(lower) || h.name.toLowerCase().includes(lower)
        );
    }, [history, searchText]);

    const combinedOptions = useMemo(() => {
        // Мапа для быстрого поиска дублей
        const historyKeySet = new Set(filteredHistory.map((h) => `${h.client}::${h.name}`));

        const serverOpts = items.filter(
            (item) => !historyKeySet.has(`${item.client}::${item.name}`)
        );

        // История
        const historyPart = filteredHistory.map((h) => ({
            value: `${h.client}::${h.name}`,
            label: `${h.client} - ${h.name}`,
            data: {client: h.client, name: h.name},
            isHistory: true
        }));

        // Основные элементы с бэка
        const serverPart = serverOpts.map((item) => ({
            value: `${item.client}::${item.name}`,
            label: `${item.client} - ${item.name}`,
            data: item,
            isHistory: false
        }));

        return [...historyPart, ...serverPart];
    }, [filteredHistory, items]);

    const handleOpenChange = useCallback(
        async (isOpen: boolean) => {
            setOpen(isOpen);

            if (isOpen) {
                if (!isDataLoaded) {
                    await loadInitialData();
                }
            } else {
                setInputValue('');
                handleSearchChange('');
            }
        },
        [isDataLoaded, loadInitialData, selectedValues, handleSearchChange]
    );

    const handleUpdate = useCallback(
        (values: string[]) => {
            setSelectedValues(values);
            if (values.length > 0) {
                const [firstValue] = values;
                const found = combinedOptions.find((opt) => opt.value === firstValue);
                if (found) {
                    setSelectedData({client: found.data.client, name: found.data.name});

                    addToHistory({client: found.data.client, name: found.data.name});

                    emit('selectClient', {
                        selectedClient: {
                            client: found.data.client,
                            name: found.data.name,
                            isSelected: true
                        }
                    });

                    emit('clientSelectionError', {isError: false});
                }
            } else {
                setSelectedData(null);

                emit('selectClient', {
                    selectedClient: {
                        client: '',
                        name: '',
                        isSelected: false
                    }
                });

                setInputValue('');
                handleSearchChange('');
            }
        },
        [combinedOptions, addToHistory, emit, handleSearchChange]
    );

    const handleLoadMore = useCallback(() => {
        loadMoreData();
    }, [loadMoreData]);

    return (
        <>
            <Select
                placeholder={
                    <Text
                        variant="body-2"
                        color={state.isClientSelectionError ? 'primary' : 'hint'}
                    >
                        Выберите клиента
                    </Text>
                }
                open={open}
                onOpenChange={handleOpenChange}
                value={selectedValues}
                onUpdate={handleUpdate}
                filterable
                hasClear
                popupWidth={'fit'}
                size={'xl'}
                loading={true && hasMore}
                onLoadMore={handleLoadMore}
                errorPlacement="inside"
                errorMessage={
                    state.isClientSelectionError && (
                        <Text variant="body-1" color="danger">
                            Клиент не выбран
                        </Text>
                    )
                }
                validationState={state.isClientSelectionError ? 'invalid' : undefined}
                className={b('select-client-main-header')}
                getOptionHeight={() => 50}
                renderSelectedOption={(option) => {
                    const data = option?.data || selectedData;

                    if (!data) {
                        return <></>;
                    }

                    const {client, name} = data;
                    return (
                        <div>
                            <span>{client}</span> / <span>{name}</span>
                        </div>
                    );
                }}
                renderEmptyOptions={() => {
                    if (isDataLoaded && combinedOptions.length === 0) {
                        return <div>Не найдено</div>;
                    }

                    return <></>;
                }}
                renderPopup={({renderList, renderFilter}) => {
                    return (
                        <div className={b('render-popup')}>
                            <div className={b('header-search')}>{renderFilter()}</div>

                            {renderList()}

                            {isLoading && (
                                <div className={b('loader-options')}>
                                    <Loader size="s" />
                                </div>
                            )}
                        </div>
                    );
                }}
                renderFilter={(props) => (
                    <TextInput
                        placeholder="Поиск"
                        value={inputValue}
                        onChange={(event) => {
                            const raw = event.target.value;
                            setInputValue(raw);
                            props.onChange(raw.trim());
                            handleSearchChange(raw.trim());
                        }}
                        onKeyDown={props.onKeyDown}
                        controlRef={props.ref}
                        style={props.style}
                    />
                )}
            >
                {combinedOptions.map((option) => {
                    return (
                        <Select.Option
                            key={option.value}
                            value={option.value}
                            text={option.label}
                            data={option.data}
                        >
                            <div>
                                <div className={b('label-container')}>
                                    <Text variant="body-1">{option.data.client}</Text>
                                    <Text variant="body-1" color="secondary">
                                        {option.data.name}
                                    </Text>
                                </div>

                                {option.isHistory}
                            </div>
                        </Select.Option>
                    );
                })}
            </Select>
        </>
    );
};
